import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import SearchIcon from '../../../../../../assets/Svg/SearchIcon';
import ReactDOM from 'react-dom';
import SideDrawer from '../../../../../../common/_custom/SideDrawer/SideDrawer';
import styles from './RelatedCompaniesNetwork.module.scss';
import {
    BLACK_DROPDOWN_CARET,
    caretRight,
    filterIconMUDark,
    filterIconMUPurpleDark,
    purpleOfficeIcon,
    purpleRedirectIcon,
} from '../../../../../../assets/hostedassets';
import Tooltip from '../../../../../../common/_custom/Tooltip/Tooltip';
import { AICA_REPORT_AVAILABLE_TOOLTIP } from '../../../../../../utils/constants/commonConstants';
import clsx from 'clsx';
import { CompaniesNetworkData, RelatedCompany, StatusOption } from './types';
import { Mixpanel } from '../../../../../../utils/mixpanel';
import { AICA_NETWORK_FILTER_APPLIED } from '../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { useAppSelector } from '../../../../../../app/hooks';

interface RelatedCompaniesNetworkDrawerProps {
    data: CompaniesNetworkData;
    statusOptions: StatusOption[];
    relationshipStatusOptions: StatusOption[];
    onClose: () => void;
    companyName?: string;
}

// ─── Utility ─────────────────────────────────────────────────────────────────

function getStatusMeta(status: string | undefined, options: StatusOption[]): StatusOption | null {
    if (!status) return null;
    return options.find((opt) => opt?.value?.toLowerCase?.() === status?.toLowerCase?.()) ?? null;
}

// ─── Filter hook ─────────────────────────────────────────────────────────────

function useDrawerFilters(tableRef: React.RefObject<HTMLDivElement>) {
    const [activeFilter, setActiveFilter] = useState<'status' | 'relationship' | null>(null);
    const [statusFilters, setStatusFilters] = useState<string[]>([]);
    const [relationshipFilters, setRelationshipFilters] = useState<string[]>([]);
    const [filterSearch, setFilterSearch] = useState('');
    const statusFilterBtnRef = useRef<HTMLButtonElement>(null);
    const relationshipFilterBtnRef = useRef<HTMLButtonElement>(null);

    // Close the active filter dropdown when the user clicks outside of it
    useEffect(() => {
        if (!activeFilter) return;
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as Node;
            // Allow clicks on the filter toggle buttons — they handle open/close themselves
            const isStatusBtn = statusFilterBtnRef.current?.contains(target);
            const isRelBtn = relationshipFilterBtnRef.current?.contains(target);
            // Allow clicks anywhere inside the dropdown panel (e.g. search input, options)
            const isDropdown = !!(e.target as Element)?.closest?.('[data-filter-dropdown]');
            if (!isStatusBtn && !isRelBtn && !isDropdown) {
                setActiveFilter(null);
                setFilterSearch('');
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [activeFilter]);

    useEffect(() => {
        const tableEl = tableRef.current;
        if (!tableEl) return;
        const handleScroll = () => {
            setActiveFilter(null);
            setFilterSearch('');
        };
        tableEl.addEventListener('scroll', handleScroll);
        return () => tableEl.removeEventListener('scroll', handleScroll);
    }, [tableRef]);

    // Opens the clicked filter dropdown, or closes it if it's already open
    const toggleFilter = (type: 'status' | 'relationship') => {
        setActiveFilter((prev) => (prev === type ? null : type));
        setFilterSearch('');
    };

    // Adds or removes a status value from the active status filters (multi-select)
    const toggleStatusFilter = (value: string) => {
        const lower = value.toLowerCase();
        setStatusFilters((prev) => (prev.includes(lower) ? prev.filter((v) => v !== lower) : [...prev, lower]));
    };

    // Adds or removes a relationship value from the active relationship filters (multi-select)
    const toggleRelationshipFilter = (value: string) => {
        const lower = value.toLowerCase();
        setRelationshipFilters((prev) => (prev.includes(lower) ? prev.filter((v) => v !== lower) : [...prev, lower]));
    };

    return {
        activeFilter,
        statusFilters,
        relationshipFilters,
        filterSearch,
        setFilterSearch,
        statusFilterBtnRef,
        relationshipFilterBtnRef,
        toggleFilter,
        toggleStatusFilter,
        toggleRelationshipFilter,
    };
}

// ─── Main component ───────────────────────────────────────────────────────────

const RelatedCompaniesNetworkDrawer: React.FC<RelatedCompaniesNetworkDrawerProps> = ({
    data,
    statusOptions,
    relationshipStatusOptions,
    onClose,
    companyName,
}) => {
    const [vendorsExpanded, setVendorsExpanded] = useState(true);
    const [customersExpanded, setCustomersExpanded] = useState(true);
    const tableRef = useRef<HTMLDivElement>(null);

    const userData = useAppSelector((state) => state.user.userData);

    const {
        activeFilter,
        statusFilters,
        relationshipFilters,
        filterSearch,
        setFilterSearch,
        statusFilterBtnRef,
        relationshipFilterBtnRef,
        toggleFilter,
        toggleStatusFilter,
        toggleRelationshipFilter,
    } = useDrawerFilters(tableRef);

    const trackFilterApplied = (filterValue: string) => {
        Mixpanel.track(AICA_NETWORK_FILTER_APPLIED, {
            userEmail: userData?.primaryEmail,
            companyName,
            pnoId: userData?.investorOrgId,
            filterValue,
        });
    };

    const handleToggleStatusFilter = (value: string) => {
        toggleStatusFilter(value);
        trackFilterApplied(value);
    };

    const handleToggleRelationshipFilter = (value: string) => {
        toggleRelationshipFilter(value);
        trackFilterApplied(value);
    };

    const vendorCount = data.vendorFor?.length ?? 0;
    const customerCount = data.customerOf?.length ?? 0;

    const filteredVendors = (data.vendorFor ?? []).filter((v) => {
        const statusMatch = statusFilters.length === 0 || statusFilters.includes(v.orgStatus?.toLowerCase() ?? '');
        const relMatch = relationshipFilters.length === 0 || relationshipFilters.includes(v.orgRelationshipStatus?.toLowerCase() ?? '');
        return statusMatch && relMatch;
    });

    const filteredCustomers = (data.customerOf ?? []).filter((c) => {
        const statusMatch = statusFilters.length === 0 || statusFilters.includes(c.orgStatus?.toLowerCase() ?? '');
        const relMatch = relationshipFilters.length === 0 || relationshipFilters.includes(c.orgRelationshipStatus?.toLowerCase() ?? '');
        return statusMatch && relMatch;
    });

    const activeFilterOptions = activeFilter === 'status' ? statusOptions : relationshipStatusOptions;
    const activeFilterValues = activeFilter === 'status' ? statusFilters : relationshipFilters;
    const visibleFilterOptions = filterSearch
        ? activeFilterOptions.filter((opt) => opt.label.toLowerCase().includes(filterSearch.toLowerCase()))
        : activeFilterOptions;

    return (
        <SideDrawer onClose={onClose} width="55%">
            <div className={styles.DrawerContent}>
                <div className={styles.DrawerHeader}>
                    <div className={styles.DrawerTitle}>Connected Entities in Network</div>
                </div>

                <div className={styles.DrawerTable} ref={tableRef}>
                    <div className={clsx(styles.DrawerTableRow, styles.DrawerTableHead)}>
                        <div className={styles.NameCol}>Related Party Name</div>
                        <ColumnHeaderWithFilter
                            label="Status"
                            filterKey="status"
                            activeFilter={activeFilter}
                            hasActiveFilters={statusFilters.length > 0}
                            btnRef={statusFilterBtnRef}
                            onToggle={() => toggleFilter('status')}
                            className={styles.StatusCol}
                        />
                        <ColumnHeaderWithFilter
                            label="Relationship Status"
                            filterKey="relationship"
                            activeFilter={activeFilter}
                            hasActiveFilters={relationshipFilters.length > 0}
                            btnRef={relationshipFilterBtnRef}
                            onToggle={() => toggleFilter('relationship')}
                            className={styles.RelationshipStatusCol}
                        />
                    </div>

                    <SectionToggleButton
                        expanded={vendorsExpanded}
                        label={`Vendors for Customers in Network (${filteredVendors.length})`}
                        onToggle={() => setVendorsExpanded((prev) => !prev)}
                        disabled={vendorCount === 0}
                    />
                    {vendorsExpanded &&
                        filteredVendors.map((vendor, index) => (
                            <EntityRow
                                key={`vendor-${index}`}
                                entity={vendor}
                                statusOptions={statusOptions}
                                relationshipStatusOptions={relationshipStatusOptions}
                            />
                        ))}

                    <SectionToggleButton
                        expanded={customersExpanded}
                        label={`Customers for Vendors/Suppliers in Network (${filteredCustomers.length})`}
                        onToggle={() => setCustomersExpanded((prev) => !prev)}
                        disabled={customerCount === 0}
                    />
                    {customersExpanded &&
                        filteredCustomers.map((customer, index) => (
                            <EntityRow
                                key={`customer-${index}`}
                                entity={customer}
                                statusOptions={statusOptions}
                                relationshipStatusOptions={relationshipStatusOptions}
                            />
                        ))}
                </div>
            </div>

            {activeFilter &&
                ReactDOM.createPortal(
                    <FilterDropdown
                        anchorRef={activeFilter === 'status' ? statusFilterBtnRef : relationshipFilterBtnRef}
                        options={visibleFilterOptions}
                        selectedValues={activeFilterValues}
                        search={filterSearch}
                        onSearchChange={setFilterSearch}
                        onToggleOption={activeFilter === 'status' ? handleToggleStatusFilter : handleToggleRelationshipFilter}
                    />,
                    document.body,
                )}
        </SideDrawer>
    );
};

export default RelatedCompaniesNetworkDrawer;

// ─── Sub-components ───────────────────────────────────────────────────────────

type ColumnHeaderWithFilterProps = {
    label: string;
    filterKey: 'status' | 'relationship';
    activeFilter: 'status' | 'relationship' | null;
    hasActiveFilters: boolean;
    btnRef: React.RefObject<HTMLButtonElement>;
    onToggle: () => void;
    className?: string;
};

const ColumnHeaderWithFilter: React.FC<ColumnHeaderWithFilterProps> = ({
    label,
    filterKey,
    activeFilter,
    hasActiveFilters,
    btnRef,
    onToggle,
    className,
}) => (
    <div className={className}>
        <span>{label}</span>
        <button ref={btnRef} type="button" className={styles.FilterBtn} onClick={onToggle} aria-label={`Filter by ${label}`}>
            <img src={hasActiveFilters || activeFilter === filterKey ? filterIconMUPurpleDark : filterIconMUDark} alt="" />
        </button>
    </div>
);

type EntityRowProps = {
    entity: RelatedCompany;
    statusOptions: StatusOption[];
    relationshipStatusOptions: StatusOption[];
};

const EntityRow: React.FC<EntityRowProps> = ({ entity, statusOptions, relationshipStatusOptions }) => {
    const [hovered, setHovered] = useState(false);
    const statusMeta = getStatusMeta(entity.orgStatus, statusOptions);
    const relStatusMeta = getStatusMeta(entity.orgRelationshipStatus, relationshipStatusOptions);
    const redirectUrl = entity.orgId
        ? `${window.location.origin}/investor/app/portfolio/${entity.orgId}?fetchType=all&page=1&type=scan&tab=aicaReport&selectedTab=overview`
        : '';

    return (
        <div className={styles.DrawerTableRow}>
            <CompanyLinkCell entity={entity} hovered={hovered} setHovered={setHovered} redirectUrl={redirectUrl} />
            <StatusCell statusMeta={statusMeta} fallbackStatus={entity.orgStatus} />
            <StatusCell statusMeta={relStatusMeta} fallbackStatus={entity.orgRelationshipStatus} />
        </div>
    );
};

type CompanyLinkCellProps = {
    entity: RelatedCompany;
    hovered: boolean;
    setHovered: (v: boolean) => void;
    redirectUrl: string;
};

const CompanyLinkCell: React.FC<CompanyLinkCellProps> = ({ entity, hovered, setHovered, redirectUrl }) => (
    <div className={styles.NameCol}>
        {entity.orgId ? (
            <Tooltip content={AICA_REPORT_AVAILABLE_TOOLTIP} direction="top" usePortal={true} fitContent={true}>
                <a
                    className={styles.DrawerCompanyLink}
                    href={redirectUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                >
                    <div className={styles.DrawerCompanyNameCell}>
                        <img src={hovered ? purpleRedirectIcon : purpleOfficeIcon} alt="" className={styles.CompanyIcon} />
                        <span className={styles.DrawerCompanyName}>{entity.orgName || '-'}</span>
                    </div>
                </a>
            </Tooltip>
        ) : (
            <div className={styles.DrawerCompanyNameCell}>
                <img src={purpleOfficeIcon} alt="" className={styles.CompanyIcon} />
                <span className={styles.DrawerCompanyName}>{entity.orgName || '-'}</span>
            </div>
        )}
    </div>
);

type StatusCellProps = {
    statusMeta: StatusOption | null;
    fallbackStatus?: string;
};

const StatusCell: React.FC<StatusCellProps> = ({ statusMeta, fallbackStatus }) => (
    <div className={styles.StatusCol}>
        {statusMeta ? (
            <span className={styles.StatusPill} style={{ backgroundColor: statusMeta.backgroundColor, color: statusMeta.labelColor }}>
                {statusMeta.label}
            </span>
        ) : (
            <span className={clsx(styles.StatusPill, styles.StatusPillFallback)}>{fallbackStatus || '-'}</span>
        )}
    </div>
);

type SectionToggleButtonProps = {
    expanded: boolean;
    label: string;
    onToggle: () => void;
    disabled?: boolean;
};

const SectionToggleButton: React.FC<SectionToggleButtonProps> = ({ expanded, label, onToggle, disabled = false }) => (
    <button type="button" className={styles.SectionRow} onClick={disabled ? undefined : onToggle} disabled={disabled}>
        <span className={styles.SectionCaret}>
            {!disabled && (expanded ? <img src={BLACK_DROPDOWN_CARET} alt="" /> : <img src={caretRight} alt="" />)}
        </span>
        <span>{label}</span>
    </button>
);

type FilterDropdownProps = {
    anchorRef: React.RefObject<HTMLButtonElement>;
    options: StatusOption[];
    selectedValues: string[];
    search: string;
    onSearchChange: (val: string) => void;
    onToggleOption: (val: string) => void;
};

const FilterDropdown: React.FC<FilterDropdownProps> = ({ anchorRef, options, selectedValues, search, onSearchChange, onToggleOption }) => {
    const [pos, setPos] = useState({ top: 0, left: 0 });
    const dropdownRef = useRef<HTMLDivElement>(null);

    const DROPDOWN_OFFSET_Y = 4; // fixed design gap between button and dropdown — not screen-size dependent
    const DROPDOWN_FALLBACK_WIDTH = 220; // only used before the dropdown renders; actual width is read from the DOM after that
    const SCREEN_EDGE_PADDING = 8; // fixed minimum buffer from viewport edges — intentionally small and consistent across screen sizes

    useLayoutEffect(() => {
        if (!anchorRef.current) return;
        const rect = anchorRef.current.getBoundingClientRect();
        const dropdownWidth = dropdownRef.current?.offsetWidth ?? DROPDOWN_FALLBACK_WIDTH;
        const left = Math.min(rect.left, window.innerWidth - dropdownWidth - SCREEN_EDGE_PADDING);
        setPos({ top: rect.bottom + DROPDOWN_OFFSET_Y, left: Math.max(SCREEN_EDGE_PADDING, left) });
    }, [anchorRef]);

    return (
        <div ref={dropdownRef} data-filter-dropdown="true" className={styles.FilterDropdown} style={{ top: pos.top, left: pos.left }}>
            <div className={styles.FilterSearchWrapper}>
                <SearchIcon width="14" height="14" stroke="#9ca3af" aria-hidden="true" />
                <input type="text" placeholder="Search" value={search} onChange={(e) => onSearchChange(e.target.value)} autoFocus />
            </div>
            <div className={styles.FilterOptionsList}>
                {options.map((opt) => (
                    <label key={opt.value} className={styles.FilterOption}>
                        <input
                            type="checkbox"
                            checked={selectedValues.includes(opt.value.toLowerCase())}
                            onChange={() => onToggleOption(opt.value)}
                        />
                        <span
                            className={styles.StatusPill}
                            style={opt.backgroundColor ? { backgroundColor: opt.backgroundColor, color: opt.labelColor } : undefined}
                        >
                            {opt.label}
                        </span>
                    </label>
                ))}
                {options.length === 0 && <div className={styles.FilterNoOptions}>No options found</div>}
            </div>
        </div>
    );
};
